import {
  Avatar as AntAvatar,
  AvatarProps as AntAvatarProps,
  theme,
} from 'antd';
import styled from 'styled-components';
import type { GlobalToken } from 'antd';
import { radius } from '../../theme/radius';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export type AvatarShape = 'circle' | 'square';
export type AvatarStatus = 'online' | 'offline' | 'busy' | 'away';

export interface AvatarProps extends Omit<AntAvatarProps, 'size' | 'shape'> {
  /** Image source URL */
  src?: string;
  /** Name to generate initials from (fallback when no src) */
  name?: string;
  /** Size of the avatar */
  size?: AvatarSize;
  /** Shape of the avatar */
  shape?: AvatarShape;
  /** Status indicator */
  status?: AvatarStatus;
  /** Custom background color (overrides default) */
  backgroundColor?: string;
}

const sizeMap: Record<AvatarSize, number> = {
  xs: 24,
  sm: 32,
  md: 40,
  lg: 48,
  xl: 64,
};

const fontSizeMap: Record<AvatarSize, number> = {
  xs: 10,
  sm: 12,
  md: 14,
  lg: 18,
  xl: 24,
};

const statusSizeMap: Record<AvatarSize, number> = {
  xs: 6,
  sm: 8,
  md: 10,
  lg: 12,
  xl: 14,
};

const getStatusColor = (token: GlobalToken, status: AvatarStatus) => {
  const statusColors = {
    online: token.colorSuccess,
    offline: token.colorBorder,
    busy: token.colorError,
    away: token.colorWarning,
  };
  return statusColors[status];
};

// Generate a consistent color based on name using token colors
const getBackgroundColor = (token: GlobalToken, name?: string): string => {
  if (!name) return token.colorPrimary;

  const colorOptions = [
    token.colorPrimary,
    token.colorInfo,
    token.colorSuccess,
    token.colorWarning,
    token.colorError,
  ];

  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (name.codePointAt(i) ?? 0) + ((hash << 5) - hash);
  }

  return colorOptions[Math.abs(hash) % colorOptions.length];
};

// Get initials from name
const getInitials = (name?: string): string => {
  if (!name) return '?';

  const words = name.trim().split(/\s+/);
  if (words.length === 1) {
    return words[0].substring(0, 2).toUpperCase();
  }

  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
};

interface StyledProps {
  $token: GlobalToken;
  $size: AvatarSize;
}

const AvatarWrapper = styled.div<{ $size: AvatarSize }>`
  position: relative;
  display: inline-flex;
  width: ${({ $size }) => sizeMap[$size]}px;
  height: ${({ $size }) => sizeMap[$size]}px;
`;

const StyledAvatar = styled(AntAvatar)<
  StyledProps & { $bgColor: string; $shape: AvatarShape }
>`
  width: ${({ $size }) => sizeMap[$size]}px;
  height: ${({ $size }) => sizeMap[$size]}px;
  line-height: ${({ $size }) => sizeMap[$size]}px;
  font-size: ${({ $size }) => fontSizeMap[$size]}px;
  font-family: ${({ $token }) => $token.fontFamily};
  font-weight: ${({ $token }) => $token.fontWeightStrong};
  background: ${({ $bgColor }) => $bgColor};
  color: ${({ $token }) => $token.colorWhite};
  border-radius: ${({ $shape, $token }) =>
    $shape === 'square' ? `${$token.borderRadius}px` : radius.circle};

  .ant-avatar-string {
    font-size: ${({ $size }) => fontSizeMap[$size]}px;
    line-height: ${({ $size }) => sizeMap[$size]}px;
  }
`;

const StatusIndicator = styled.span<StyledProps & { $status: AvatarStatus }>`
  position: absolute;
  bottom: 0;
  right: 0;
  width: ${({ $size }) => statusSizeMap[$size]}px;
  height: ${({ $size }) => statusSizeMap[$size]}px;
  border-radius: ${radius.circle};
  background: ${({ $token, $status }) => getStatusColor($token, $status)};
  border: 2px solid ${({ $token }) => $token.colorWhite};
  box-sizing: content-box;
`;

/**
 * Avatar component based on Libertify Design System
 *
 * Displays a user avatar with image, initials, or icon fallback.
 * Supports status indicators and multiple sizes.
 * Now uses Ant Design theme tokens for consistent styling.
 *
 * @example
 * ```tsx
 * import { Avatar } from '@libertify/ui';
 *
 * // With image
 * <Avatar src="/user.jpg" size="md" />
 *
 * // With initials
 * <Avatar name="John Doe" size="lg" />
 *
 * // With status
 * <Avatar name="Jane Smith" status="online" />
 * ```
 */
export const Avatar = ({
  src,
  name,
  size = 'md',
  shape = 'circle',
  status,
  backgroundColor,
  icon,
  ...props
}: AvatarProps) => {
  const { token } = theme.useToken();
  const bgColor = backgroundColor || getBackgroundColor(token, name);
  const initials = getInitials(name);

  const renderAvatar = () => {
    const commonProps = {
      $token: token,
      $size: size,
      $bgColor: bgColor,
      $shape: shape,
      ...props,
    };

    if (src) {
      return <StyledAvatar {...commonProps} src={src} />;
    }

    if (icon) {
      return <StyledAvatar {...commonProps} icon={icon} />;
    }

    return <StyledAvatar {...commonProps}>{initials}</StyledAvatar>;
  };

  const avatarContent = renderAvatar();

  if (status) {
    return (
      <AvatarWrapper $size={size}>
        {avatarContent}
        <StatusIndicator $token={token} $status={status} $size={size} />
      </AvatarWrapper>
    );
  }

  return avatarContent;
};

export default Avatar;
